import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

/**
 * The code step of a Google, Microsoft or Apple sign-in whose provider did not vouch for the email (a work or school
 * account without a verified domain). The API emailed a 6-digit code to that address; this browser holds the pending
 * sign-in in an HttpOnly cookie, so the address arrives only as router state, never in the URL.
 */
export default function OAuthConfirmPage() {
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const email = (useLocation().state as { email?: string } | null)?.email
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [expired, setExpired] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await authApiService.oauthConfirm(code)
      if (res?.requiresMfa) { navigate('/mfa'); return }
      await refresh()
      navigate('/app/dashboard')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'oauth_confirm_expired') setExpired(true)
      setError(err instanceof ApiError ? err.message : 'Verification failed')
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.background, padding: spacing.md }}>
      <div style={{ background: '#fff', borderRadius: radius.lg, boxShadow: shadow.lg, padding: spacing.xl, width: '100%', maxWidth: 380 }}>
        <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xs }}>
          Confirm your email
        </div>
        <div style={{ fontSize: fontSize.base, color: colors.textSecondary, marginBottom: spacing.xl }}>
          {email ? <>We sent a 6-digit code to <strong>{email}</strong>.</> : 'We sent a 6-digit code to your email address.'}{' '}
          Your sign-in provider has not verified this address, so the code confirms it is yours.
        </div>

        {error && (
          <div role="alert" style={{ background: colors.errorLight, color: colors.error, border: '1px solid #fecaca', borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
            {error}
          </div>
        )}

        {expired ? (
          <Link to="/login" style={{ display: 'block', textAlign: 'center', color: colors.primary, fontSize: fontSize.base, fontWeight: 600 }}>
            Back to sign in
          </Link>
        ) : (
          <form onSubmit={handleSubmit}>
            <label htmlFor="oauth-confirm-code" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, color: colors.text, marginBottom: spacing.xs }}>
              Verification code
            </label>
            <input
              id="oauth-confirm-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
              required
              autoFocus
              style={{ width: '100%', padding: '9px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '20px', letterSpacing: '0.3em', textAlign: 'center', color: colors.text, boxSizing: 'border-box', outline: 'none', marginBottom: spacing.md }}
            />
            <button
              type="submit"
              disabled={loading || code.length < 6}
              style={{ width: '100%', padding: '10px', background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, fontSize: fontSize.base, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading || code.length < 6 ? 0.7 : 1 }}
            >
              {loading ? 'Verifying...' : 'Continue'}
            </button>
            <div style={{ textAlign: 'center', marginTop: spacing.lg }}>
              <Link to="/login" style={{ color: colors.primary, fontSize: fontSize.sm }}>Cancel</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
